import { Card, Col, Row, Spin, Button } from 'antd';
import { DealershipChooser } from 'components';
import { get } from 'lodash';
import { observe } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { withRouter } from 'react-router';
import styled from 'styled-components';
import { CarStatusStore, ShopStore } from 'stores';

import { FiltersFormWithVVA } from 'components/FleetReports/FiltersFormWithVVA';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { downloadCSV } from 'shared/utils';
import { notifications } from '../../helpers/notificationHelper';
import { ExportOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const H2Styled = styled.h2`
  font-size: 1.2rem;
`;

class AssetDurationPage extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object,
  };

  state = {
    isInitialState: true,
    isLoadingAssetDuration: false,
    assetDurationData: [],
    dateRange: [dayjs().subtract(30, 'day'), dayjs()],
    limit: 20,
    status: [],
    selectedStatusDescription: '',
    searchText: '', // State for storing search input
    isLoadingInitialFilterData: false,
    filter: {
      status: [],
      vendor: undefined,
      assetType: undefined,
      make: undefined,
      model: undefined,
      year: undefined,
      carIds: undefined,
    },
    shopStatuses: [],
  };

  async componentDidMount() {
    await Promise.all([
      this.loadInitialFilterData(),
      this.loadDataForAssetDuration(),
    ]);
  }

  componentWillUnmount() {
    if (this.disposer) {
      this.disposer();
    }
  }

  disposer = observe(ShopStore.currentShop, 'id', async () => {
    await Promise.all([
      this.loadInitialFilterData(),
      this.loadDataForAssetDuration(),
    ]);
  });

  loadInitialFilterData = async () => {
    this.setState({ isLoadingInitialFilterData: true });
    const [shopStatuses] = await Promise.all([
      CarStatusStore.fetchShopCarStatuses(ShopStore.currentShop.id),
    ]);

    // Set the default status to the first item in shopStatuses
    let filter = { ...this.state.filter };
    filter.status =
      shopStatuses && shopStatuses.length > 0 && shopStatuses[0].key
        ? [shopStatuses[0].key]
        : ['running'];

    this.setState(
      {
        shopStatuses: shopStatuses || [],
        selectedStatusDescription:
          (shopStatuses[0] && shopStatuses[0].description) || '',
        filter: filter,
      },
      () => {
        this.loadDataForAssetDuration();
      }
    );
  };

  loadDataForAssetDuration = async () => {
    try {
      this.setState({ isLoadingAssetDuration: true });
      const {
        dateRange,
        limit,
        searchText,
        filter: { status, vendor, assetType, make, model, year, carIds },
      } = this.state;

      let [startDate, endDate] = dateRange;

      const response = await ShopStore.getAssetDurationData(
        ShopStore.currentShop.id,
        startDate,
        endDate,
        limit,
        status,
        searchText,
        {
          assetType: assetType,
          vendor: vendor,
          make: make,
          model: model,
          year: year,
          carIds: carIds,
        }
      );

      const data = get(response, 'data.assets', []);

      if (Array.isArray(data) && data.length > 0) {
        this.setState({
          assetDurationData: data,
        });
      } else {
        if (!this.state.isInitialState) {
          notifications.warning(
            'No asset duration data available for the selected filters.'
          );
        }
        this.setState({ assetDurationData: [] });
      }
    } catch (error) {
      if (this.state.filter.status.length === 0) {
        notifications.warning('Please select at least one status to filter.');
      } else {
        notifications.error(
          `Error loading asset duration data: ${error.message || 'Unknown error'}`
        );
      }
      this.setState({ assetDurationData: [] });
    } finally {
      this.setState({ isLoadingAssetDuration: false, isInitialState: false });
    }
  };

  deburrLoadAssetDuration = null;

  handleFilterChange = (changedFields) => {
    if (
      changedFields.filter &&
      changedFields.filter.status &&
      Array.isArray(changedFields.filter.status)
    ) {
      const selectedStatuses = this.state.shopStatuses.filter((status) =>
        changedFields.filter.status.includes(status.key)
      );
      this.setState({
        ...changedFields,
      });
    } else {
      this.setState(changedFields);
    }

    // call deburrLoadAssetDuration
    if (changedFields.limit || changedFields.dateRange) {
      if (this.deburrLoadAssetDuration) {
        clearTimeout(this.deburrLoadAssetDuration);
      }
      this.deburrLoadAssetDuration = setTimeout(() => {
        this.loadDataForAssetDuration();
      }, 500);
    }
  };

  handleSearchTextChange = (e) => {
    this.setState({ searchText: e.target.value }, () => {
      this.loadDataForAssetDuration(); // Automatically call API when search text changes
    });
  };

  handleApplyFilters = () => {
    const { filter, shopStatuses } = this.state;
    const selectedStatuses = shopStatuses.filter((status) =>
      (filter.status || []).includes(status.key)
    );
    this.setState(
      {
        selectedStatusDescription: selectedStatuses
          .map((status) => status.description)
          .join(', '),
      },
      () => {
        this.loadDataForAssetDuration();
      }
    );
  };

  handleExportToCSV = async () => {
    // Show a loading notification
    const notificationId = notifications.loading(
      'Exporting Asset Duration data to CSV... You can keep using the app while this is happening.'
    );

    try {
      const {
        dateRange,
        limit,
        searchText,
        filter: { status, vendor, assetType, make, model, year, carIds },
      } = this.state;
      let [startDate, endDate] = dateRange;
      const response = await ShopStore.getAssetDurationDataCSV(
        ShopStore.currentShop.id,
        startDate,
        endDate,
        limit,
        status,
        searchText,
        {
          assetType: assetType,
          vendor: vendor,
          make: make,
          model: model,
          year: year,
          carIds: carIds,
        }
      );

      const fileName = `Asset Duration Data - ${new Date().toLocaleDateString()}`;
      const csvData = response?.data || [];
      downloadCSV(csvData, fileName, false);

      // Update notification to success
      notifications.success('Asset Duration data exported successfully!', {
        id: notificationId,
      });
    } catch (error) {
      // Update notification to error
      const errorMessage = error?.message || 'Unknown error occurred';
      notifications.error(`Failed to export CSV: ${errorMessage}`, {
        id: notificationId,
      });
      this.setState({ assetDurationData: [] });
    }
  };

  handleResetFilters = () => {
    // Get the default status from the shopStatuses
    const { shopStatuses } = this.state;
    const defaultStatus =
      shopStatuses && shopStatuses.length > 0 && shopStatuses[0].key
        ? [shopStatuses[0].key]
        : ['running'];

    const defaultDescription =
      (shopStatuses.find((s) => s.key === defaultStatus[0]) || {})
        .description || '';

    this.setState(
      {
        filter: {
          status: defaultStatus, // Reset to default status
          vendor: undefined,
          assetType: undefined,
          make: undefined,
          model: undefined,
          year: undefined,
          carIds: undefined,
        },
        status: ['running'],
        selectedStatusDescription: defaultDescription,
      },
      () => {
        this.loadDataForAssetDuration();
      }
    );
  };

  render() {
    const {
      assetDurationData,
      timePeriod,
      dateRange,
      isLoadingAssetDuration,
      selectedStatusDescription,
      limit,
      filter: { status },
    } = this.state;

    const [startDate, endDate] = this.state.dateRange;
    const diff = endDate.diff(startDate, 'day');

    const uniqueAssets = [];
    const assetMap = {};
    assetDurationData.forEach((item) => {
      if (!assetMap[item.asset_id]) {
        uniqueAssets.push({
          asset_id: item.asset_id,
          asset_name: item.asset_name,
          asset_type: item.asset_type,
        });
        assetMap[item.asset_id] = true;
      }
    });
    const uniqueStatuses = [
      ...new Set(assetDurationData.map((item) => item.status)),
    ];

    const statusDescriptionMap = {};
    const statusColourMap = {};
    (this.state.shopStatuses || []).forEach((s) => {
      statusDescriptionMap[s.key] = s.description;
      statusColourMap[s.key] = s.color;
    });

    const assetTotals = uniqueAssets.map((asset) =>
      assetDurationData
        .filter((item) => item.asset_id === asset.asset_id)
        .reduce((sum, item) => sum + (item.duration_in_days || 0), 0)
    );

    const yAxisMax = Math.max(...assetTotals, 0) + 2;

    const series = uniqueStatuses.map((status) => ({
      name: statusDescriptionMap[status] || status,
      data: uniqueAssets.map((asset) => {
        const found = assetDurationData.find(
          (item) => item.asset_id === asset.asset_id && item.status === status
        );
        return {
          y: found ? found.duration_in_days : null,
          assetId: asset.asset_id,
          color: statusColourMap[status],
        };
      }),
      color: statusColourMap[status],
    }));

    const setColumnNumberColor = (color) => {
      console.log('color', color);
      if (color.startsWith('#')) {
        let r = 0,
          g = 0,
          b = 0;
        if (color.length === 7) {
          r = parseInt(color.slice(1, 3), 16);
          g = parseInt(color.slice(3, 5), 16);
          b = parseInt(color.slice(5, 7), 16);
        }
        let luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        return luminance < 128 ? '#FFFFFF' : '#333333';
      }
      return '#333333'; // Default
    };

    const assetDurationChartOptions = {
      chart: {
        type: 'column',
        marginTop: 20, // avoid number labels being cut off
      },
      tooltip: {
        formatter: function () {
          const percentage = Math.round((this.y / diff) * 100);
          const color = this.series.color;
          const labelColor = setColumnNumberColor(color);
          return `${this.key}<br/>
            <span style=“color:${labelColor}“>\u25CF</span> ${this.series.name}: <b>${this.y}</b> (${percentage}%)`;
        },
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: uniqueAssets.map(
          (item) => `${item.asset_name} (${item.asset_type})`
        ),
        crosshair: true,
        title: {
          text: 'Unit ID',
        },
      },
      yAxis: {
        min: 0,
        max: yAxisMax,
        title: {
          text: 'No. of Days',
        },
        tickInterval: 1,
      },
      series: series,
      plotOptions: {
        column: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            formatter: function () {
              return this.y;
            },
            style: {
              textAlign: 'center',
              // color: setColumnNumberColor(this.color),
              fontWeight: 'bold',
            },
            y: -5,
            crop: false,
            overflow: 'allow',
          },
          borderRadius: 3,
          pointPadding: 0.2,
        },
        series: {
          cursor: 'pointer',
          point: {
            events: {
              click: (options) => {
                // Construct the URL dynamically
                const vehicleId = options?.target?.point?.assetId;
                if (vehicleId) {
                  const customUrl = `/car/${vehicleId}/work-orders`;
                  this.props.history.push(customUrl);
                }
              },
            },
          },
        },
      },
      // series: [
      //   {
      //     name: selectedStatusDescription || '',
      //     data: assetDurationData.map((item) => {
      //       return {
      //         y: item.duration_in_days,
      //         assetId: item.asset_id,
      //         assetType: item.asset_type,
      //       };
      //     }),
      //     color: '#183cbc',
      //     timePeriod: diff,
      //   },
      // ],
    };

    return (
      <Fragment>
        <Row gutter={[8, 8]} justify="space-between" align="middle">
          <Col span={20}>
            <h1>Asset Status Duration</h1>
          </Col>
        </Row>
        <DealershipChooser />
        <Row gutter={[8, 8]} justify="space-between" align="middle">
          <Col span={20}>
            <FiltersFormWithVVA
              filter={{
                filter: this.state.filter,
              }}
              dateRange={this.state.dateRange}
              timePeriod={timePeriod}
              limit={limit}
              onChange={this.handleFilterChange}
              onReset={this.handleResetFilters}
              onApply={this.handleApplyFilters}
              hideVMRSFilter={true}
            />
          </Col>
          <Col span={4} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              onClick={this.handleExportToCSV}
              icon={<ExportOutlined />}
            >
              Export to CSV
            </Button>
          </Col>
        </Row>
        <Row style={{ marginTop: '10px' }} gutter={[8, 8]}>
          <Col span={24}>
            <Spin spinning={isLoadingAssetDuration}>
              <Card>
                <Row
                  gutter={[8, 8]}
                  justify="space-between"
                  align="middle"
                ></Row>
                <Row gutter={[8, 8]} style={{ marginTop: '10px' }}>
                  <Col span={24}>
                    {assetDurationData.length > 0 ? (
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={assetDurationChartOptions}
                      />
                    ) : (
                      <p>
                        {this.state.isInitialState
                          ? 'To start, click the filters button above and apply at least one Status filter to see any data here.'
                          : 'No data available for the selected filters.'}
                      </p>
                    )}
                  </Col>
                </Row>
              </Card>
            </Spin>
          </Col>
        </Row>
      </Fragment>
    );
  }
}

export default withRouter(observer(AssetDurationPage));
